import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { memo, useCallback, useMemo } from 'react'

import { BasicInfoFields, PartnerHierarchyType } from 'clickNgo/entities/partner/configs'
import { PartnerFlagRequestAction } from 'clickNgo/entities/partner/models'
import { IPartnerFlag } from 'clickNgo/entities/partner/models'
import { PartnerFlags } from 'clickNgo/entities/partner/ui/partnerFlags'
import { InfoBlock, InfoBlockType } from 'cng-common/components/infoBlock'
import { RenderWhen } from 'cng-common/components/renderWhen'
import { updateFormData, useGetFormData, useGetFormDataField } from 'cng-common/features/form'

import { PartnerEditorFormKeys, PartnerEditorFormSettings } from '../../../configs'
import { useReadOnlyMenuItem } from '../../../hooks/useReadOnlyMenuItem'
import { IPartnerEditorData } from '../../../models'
import { partnerEditorTranslation } from '../../../translation'
import { PartnerFormContainer } from '../../partnerFormContainer/PartnerFormContainer'

import { IWrappedPartnerFlagsProps } from './models'

import styles from './WrappedPartnerFlags.module.scss'

export const WrappedPartnerFlags = memo<IWrappedPartnerFlagsProps>(
  ({ partner, partnerFlags, title, type }) => {
    const { isReadOnly } = useReadOnlyMenuItem()
    const dispatch = useAppDispatch()
    const [actualData, updateActualData] = useGetFormDataField<IPartnerEditorData>(
      PartnerEditorFormKeys.Setting,
      PartnerEditorFormSettings.ActualData
    )

    const [hierarchyType] = useGetFormDataField<PartnerHierarchyType>(
      PartnerEditorFormKeys.BasicInfo,
      BasicInfoFields.HierarchyType
    )

    const isPartnerFlagReadOnly = isReadOnly || hierarchyType !== PartnerHierarchyType.HEADQUARTERS

    const formKey = PartnerEditorFormKeys[type]
    const [{ formData }] = useGetFormData(PartnerEditorFormKeys[type], partnerFlags)

    const requestPartnerFlags = useMemo(
      () => [...formData, ...actualData.partnerFlags.agreement],
      [actualData.partnerFlags.agreement, formData]
    )

    const handleOnCreate = useCallback(
      async (flag: IPartnerFlag) => {
        const newPartnerFlags = [flag, ...formData]
        dispatch(updateFormData({ formKey, formData: newPartnerFlags }))
        updateActualData({
          ...actualData,
          partnerFlags: {
            ...actualData.partnerFlags,
            [type.toLowerCase()]: newPartnerFlags,
          },
        })
      },
      [formData, dispatch, formKey, updateActualData, actualData, type]
    )

    const handleOnDelete = useCallback(
      async (flag: IPartnerFlag) => {
        const isNewFlag = flag.action === PartnerFlagRequestAction.Add
        const newPartnerFlags = isNewFlag
          ? formData.filter(({ code }) => code !== flag.code)
          : formData.map((partnerFlag) => {
              if (partnerFlag.code === flag.code) {
                return {
                  ...partnerFlag,
                  action: PartnerFlagRequestAction.Remove,
                  status: undefined,
                  newEffectiveDate: undefined,
                }
              }

              return partnerFlag
            })
        dispatch(updateFormData({ formKey, formData: newPartnerFlags }))
        updateActualData({
          ...actualData,
          partnerFlags: {
            ...actualData.partnerFlags,
            [type.toLowerCase()]: newPartnerFlags,
          },
        })
      },
      [formData, dispatch, formKey, updateActualData, actualData, type]
    )

    const handleOnUpdate = useCallback(
      async (flag: IPartnerFlag) => {
        const newPartnerFlags = formData.map((partnerFlag) =>
          partnerFlag.code === flag.code ? flag : partnerFlag
        )

        dispatch(updateFormData({ formKey, formData: newPartnerFlags }))
        updateActualData({
          ...actualData,
          partnerFlags: {
            ...actualData.partnerFlags,
            [type.toLowerCase()]: newPartnerFlags,
          },
        })
      },
      [actualData, dispatch, formData, formKey, type, updateActualData]
    )

    return (
      <PartnerFormContainer partner={partner} title={title}>
        <RenderWhen condition={isPartnerFlagReadOnly}>
          <InfoBlock
            text={partnerEditorTranslation.warning.readOnlyPartnerFlags}
            type={InfoBlockType.Warning}
          />
        </RenderWhen>
        <div className={styles.content}>
          <PartnerFlags
            partner={partner}
            partnerFlags={requestPartnerFlags}
            type={type}
            isDisabled={isPartnerFlagReadOnly}
            onCreate={handleOnCreate}
            onDelete={handleOnDelete}
            onUpdate={handleOnUpdate}
          />
        </div>
      </PartnerFormContainer>
    )
  }
)
